import { debounce } from "../../utils/common";
import { Component, register } from "../../core";
import {addEvent} from '../../utils/gaService'

/**
 * Partial templates for this component
 */
const partials = {
    // poisuggestions template
    poisuggestions: (poisuggestions = []) => poisuggestions.reduce((html, poi) => html + `
        <div class="input-div show ${poi.selected ? 'selected' : ''}" title="${poi.poiName}" data-ref="poisuggestions" data-key="${poi.id}">
            <input class="poisuggestions" type="checkbox" name="${poi.poiName}" id="${poi.id}" ${poi.selected ? 'checked' : ''} />
            <label for="poi-${poi.id}">${poi.poiName}</label>
        </div>
       `, ''),
};

/**
 * Template for the component
 * 
 * @param {Object} state the state of this component
 */
const template = (state) => `
	<div data-ref="poi-search">
        <span>Nearby Landmarks</span>
		<div class="search">
			<input type="text" data-ref="poisearch" placeholder="Search for POIs" />
		</div>
        <div data-ref="poisuggestions" class="clearfix scrollbox">
                ${partials.poisuggestions(state.topSuggestions)}          
        </div>
	</div>
`;

/**
 * The initial state of this component
 */
const initialState = {
    cityId: null,
    poisuggestions: [],
    topSuggestions: [],
    selectedpois: [], 
};

/**
 *  "Cowork poi" filter component
 * 
 * @extends Component
 * @author Saravanan
 */
export class CoworkPoiSearchComponent extends Component {
    
    static alias = 'poi-filter';
    static template = template;
 
    constructor(el, state) {
        super(el, {...initialState, ...state});
    }

    /**
     * Lifecycle hook that is trigerred after component has loaded
     */
    onComponentLoaded() {
        
        // The event handlers has to be explicitly bound to `this` scope
        this.onClickSelectSuggestion = this.onClickSelectSuggestion.bind(this);
        this.onChangeLoadSuggestions = this.onChangeLoadSuggestions.bind(this);
        this.handleDropdownClick = this.handleDropdownClick.bind(this);

        // Initialize the event handlers
        this.refs.poisearch.addEventListener('input', debounce(this.onChangeLoadSuggestions, 300));
        this.refs.poisuggestions.addEventListener('click', this.onClickSelectSuggestion);
        //this.delegate('click', this.refs.poisuggestions, { 'suggestion': this.onClickSelectSuggestion });

        // Load the top places of interest for the current city
        this.loadTopSuggestions(this.state.cityId);

        this.refs.poisuggestions.addEventListener('click', this.handleDropdownClick);
    }

    /**
     * Event handler to load poi suggestions against the query
     * 
     * @param {KeyboardEvent} event the `input` event
     */
    onChangeLoadSuggestions(event) {
        const {target} = event;
        const query = target.value.trim();

        if (query) {
            // Load the poisuggestions and render them
            this.loadSuggestions(query, this.state.cityId);
        } else {
            // Render the top poisuggestions if nothing to search
            this.renderSuggestions(this.state.topSuggestions);
        }
    }

    /**
     * Event handler to that delegates `click` event on the poisuggestions list
     * 
     * @param {MouseEvent} event the `click` event
     * @param {Object} data the attribute dataset
     */
    onClickSelectSuggestion(event) {
        let eventname = event.target.htmlFor;
        if(typeof eventname != 'undefined'){
            let getId = eventname.split("-");
            
            // Check if the poi is already selected
            const selectedpois = [...this.state.selectedpois];
            const idxSelectedpois = selectedpois.findIndex((poi) =>poi.id == getId[1]);

            // Unselect if already selected
            if (idxSelectedpois >= 0) {
                // Remove the poi from the selected list
                selectedpois.splice(idxSelectedpois, 1);
            } else {
                // Find the selected poi id from the poisuggestions
                const selectedpoi = this.state.poisuggestions.find((poi) => poi.id == getId[1]);

                // Add the poi to the list of selected ids
                if (selectedpoi) {
                    selectedpoi.selected = true;
                    selectedpois.push(selectedpoi);
                } 
            }

            // Update the state
            this.setState({
                ...this.state,
                selectedpois: selectedpois,
                poisuggestions: this.mapToSelectedpois(this.state.poisuggestions, selectedpois)
            });
        }

        // Render the poisuggestions and emit an event to notify about this selection 
        this.renderSuggestions(this.state.poisuggestions);
        this.emit('poi.selected', this.state.selectedpois);
    }

    /**
     * Load the top poisuggestions for the provided city
     * 
     * @param {number} cityId realestate city id
     */
    loadTopSuggestions(cityId) {
        this.fetchTopSuggestions(cityId)
            .then((topSuggestions) => this.mapToSelectedpois(topSuggestions))
            .then((topSuggestions) => {
                // Update the state
                this.setState({
                    ...this.state, 
                    topSuggestions,
                    poisuggestions: topSuggestions
                });

                // Render the top poisuggestions
                this.renderSuggestions(this.state.topSuggestions);
            });
    }

    /**
     * Loads the poisuggestions for the given query
     * 
     * @param {string} query the poi name query
     * @param {string} cityId realestate city id
     */
    loadSuggestions(query, cityId) {
        this.fetchSuggestions(query, cityId)
            .then((poisuggestions) => this.mapToSelectedpois(poisuggestions))
            .then((poisuggestions) => {
                // Update the state
                this.setState({...this.state, poisuggestions});
                
                // Render poisuggestions
                this.renderSuggestions(this.state.poisuggestions);
            });
    }

    /**
     * Fetches the top poisuggestions for the given city
     * 
     * @param {string} cityId realestate city id 
     */
    fetchTopSuggestions(cityId) {
        //console.log(cityId);
        return fetch(`/homes/re2/poi/top?cityId=${cityId}&source=qh`).then((res) => res.json());
    }

    /**
     * Fetches the poisuggestions for the given query
     * 
     * @param {string} query the poi name query
     * @param {string} cityId realestate city id
     */
    fetchSuggestions(query, cityId) {
    	return fetch(`/homes/re2/poi/suggest?query=${query}&cityId=${cityId}`).then((res) => res.json());
    }

    renderSpinner(show = true) {
        if (show) {
            this.refs.spinner.classList.remove('hide');
        } else {
            this.refs.spinner.classList.add('hide');
        }
    }

    /**
     * Renders the places of interest partial
     * 
     * @param {Array} poisuggestions 
     */
    renderSuggestions(poisuggestions = []) {
        if(poisuggestions && poisuggestions.length > 0){
            this.refs.poisuggestions.innerHTML = partials.poisuggestions(poisuggestions);
        }else{
            document.getElementById('poiFilter').classList.add('hide');
        }
        
    }

    /**
     * Renders the selected pois
     * 
     * @param {Array} Selectedpois the selected pois
     */
    renderSelectedpois(selectedpois = []) {
        this.refs.selection.innerHTML = partials.poisuggestions(selectedpois);
    }

    /**
     * Maps the pois to selected pois
     * 
     * @param {Array} pois the places of interest
     * @param {Array} Selectedpois the selected pois
     */
    mapToSelectedpois(pois = [], selectedpois = this.state.selectedpois) {
        // Filter all the pois 
        const toppois = pois.map((poi) => ({...poi, selected: selectedpois.findIndex((spoi) => spoi.id == poi.id) >= 0}));
        const restpois = selectedpois.filter((spoi) => spoi.poiName).filter((spoi) => -1 === pois.findIndex((poi) => spoi.id == poi.id));

        // Merge both the lists
        return [...restpois, ...toppois];
    }

    handleDropdownClick(ev) {
    	event.stopPropagation()
    }

}

// Register this component
register(CoworkPoiSearchComponent);